import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import theme from "../shared/styles/muiTheme.js";
import { createBrowserHistory } from "history";
import React from "react";
import ReactDOM from "react-dom";
import { Route, Router, Switch } from "react-router-dom";

import {
  builderIOInit,
  identifyUser,
  posthogInit,
  posthogStartSessionRecording,
} from "../shared/utils/analytics";

import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  Observable,
} from "@apollo/client";
import { InMemoryCache } from "@apollo/client/cache";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

const httpLink = createHttpLink({
  uri: "/me/gql/graphql",
  credentials: "same-origin", // send up cookies for authentication
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
    },
  };
});

// An expired or absent session makes the patient GraphQL endpoint return 401
// (previously an empty body, which Apollo reported as the misleading "JSON Parse
// error: Unexpected EOF"). Either way Apollo propagates it as an uncaught
// observable error and Bugsnag's global handler auto-reports it. AuthRequiredRoute
// already redirects unauthenticated users to login, so here we simply swallow the
// auth failure — return an observable that never emits, keeping the racing query
// in its loading state until the redirect unmounts it. Non-auth network errors
// (5xx, offline) are left to propagate and report as before.
const authErrorLink = onError(({ networkError }) => {
  const status = networkError && networkError.statusCode;
  if (status === 401 || status === 403) {
    return new Observable(() => {});
  }
});

const client = new ApolloClient({
  link: authErrorLink.concat(authLink.concat(httpLink)),
  cache: new InMemoryCache(),
});

import "./styles/material-overrides.css";

import indexRoutes from "./routes/index.js";


builderIOInit();

posthogInit();
identifyUser();
posthogStartSessionRecording();

const hist = createBrowserHistory();

// Track protected page loads with Converge
const trackProtectedPageLoad = (location) => {
  let pathname = location.pathname || window.location.pathname;

  // List of paths to track as protected page loads
  const protectedPaths = [
    "/me",
    "/me/portal",
    "/me/refills",
    "/me/login",
    "/me/password-reset",
    "/me/messages/care",
    "/me/payment-portal",
  ];

  // Normalize pathname: remove trailing slash and query string
  pathname = pathname.replace(/\/$/, "").split("?")[0];

  // Check if current path matches any protected paths (exact match)
  const isProtectedPath = protectedPaths.includes(pathname);

  if (isProtectedPath && window.ConvergeTracking) {
    console.log("[Converge] Tracking $protected_page_load for:", pathname);
    window.ConvergeTracking.track("$protected_page_load");
  }
};

// Track initial page load
trackProtectedPageLoad(hist.location);

// Listen for route changes
hist.listen((location) => {
  trackProtectedPageLoad(location);
});

ReactDOM.render(
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <ApolloProvider client={client}>
        <Router history={hist}>
          <Switch>
            {indexRoutes.map((prop, key) => {
              return (
                <Route path={prop.path} component={prop.component} key={key} />
              );
            })}
          </Switch>
        </Router>
      </ApolloProvider>
    </ThemeProvider>
  </StyledEngineProvider>,
  document.getElementById("portal_container"),
);
